import React, { Component, Fragment } from 'react';
import Helmet from 'react-helmet';

import { withShareDB } from '../providers/ShareDBProvider';
import {
  BlockSelector,
  ShowModeSelector,
  SHOW_WORKBOOK,
  SHOW_ONLY_PINNED,
} from '../components/Selector';
import Worksheet from '../components/Worksheet';
import { CodeKernelFooter } from './CodeKernelFooter';
import { SHAREDB_COLLECTION } from '../config';
import Blocks from './Blocks';
import ColumnWrapper from '../components/ColumnWrapper';

// After we updated the MKE forum/cb to MU, it started creating all new docs with `mu-*`
// session keys and accessing them with `mu-*` session keys. Since there are still docs with
// `mke-*` session keys, we search for either the mke or the mu ones when an `mu-*` key is passed.
const buildFilters = (sessionKey) => {
  if (sessionKey?.match(/^mu-.*$/)) {
    const mkeSessionKey = sessionKey.replace('mu-', 'mke-');
    return {
      $or: [{ session_key: sessionKey }, { session_key: mkeSessionKey }],
      deprecated: { $ne: true },
    };
  }
  return { session_key: sessionKey, deprecated: { $ne: true } };
};

class RollupWorkbook extends Component {
  state = {
    docs: [],
    rollupState: {},
  };

  registry = {};

  listening = {};

  componentDidMount() {
    const { sharedb, currentUser } = this.props;
    const query = sharedb.createSubscribeQuery(
      SHAREDB_COLLECTION,
      buildFilters(currentUser.rollup),
      {}
    );

    this.rollupDoc = sharedb.get(
      SHAREDB_COLLECTION,
      `${currentUser.rollup}-rollup-doc`
    );
    this.rollupDoc.subscribe(() => {
      this.setState({ rollupState: this.rollupDoc.data });
      this.rollupDoc.on('op', () => {
        this.setState({ rollupState: this.rollupDoc.data });
      });
    });

    query.once('ready', () => {
      query.results.forEach((x) => {
        this.listening[x.id] = true;
        x.subscribe(() => {
          const { docs } = this.state;
          this.setState({ docs: [...docs, x] });
        });
        x.on('op', (ops) =>
          ops.forEach((op) => {
            const path = op.p[0];
            if (this.registry[x.id] && this.registry[x.id][path]) {
              this.registry[x.id][path].forEach((fn) => fn(x.data[path]));
            }
          })
        );
      });
    });
    query.on('insert', (docs) => {
      docs.forEach((x) => {
        if (this.listening[x.id]) {
          return;
        }
        this.listening[x.id] = true;
        x.subscribe(() => {
          const { docs } = this.state;
          this.setState({ docs: [...docs, x] });
        });
        x.on('op', (ops) =>
          ops.forEach((op) => {
            const path = op.p[0];
            if (this.registry[x.id][path]) {
              this.registry[x.id][path].forEach((fn) => fn(x.data[path]));
            }
          })
        );
      });
    });

    query.on('remove', (removedDocs) => {
      removedDocs.forEach((x) => {
        this.listening[x.id] = false;
        x.destroy();
      });
      const idsToDelete = removedDocs.map((x) => x.id);
      const { docs } = this.state;
      const newDocs = docs.filter((x) => !idsToDelete.includes(x.id));
      this.setState({ docs: newDocs });
    });
  }

  selectBlock = (block) => {
    this.rollupDoc.submitOp({ p: ['filterQuestion'], oi: block });
  };

  changePinState = (identifier, state) => {
    this.rollupDoc.submitOp({ p: ['pins', identifier], oi: state });
  };

  setShowMode = (state) => {
    this.rollupDoc.submitOp({ p: ['showMode'], oi: state });
  };

  register = (uuid, blockUuid, cb) => {
    if (!this.registry[uuid]) {
      this.registry[uuid] = {};
    }
    if (!this.registry[uuid][blockUuid]) {
      this.registry[uuid][blockUuid] = [];
    }
    this.registry[uuid][blockUuid].push(cb);
  };

  render() {
    const { template, currentUser, doc, data } = this.props;
    const { docs, rollupState } = this.state;
    return docs && rollupState ? (
      <div className="page flex flex-column">
        <Helmet>
          <title>{template.title}</title>
        </Helmet>
        <ColumnWrapper className="worksheet-column-wrapper">
          {currentUser.isInstructor && (
            <div
              className="mt5 pl5 pr5"
              style={{ display: 'flex', flexDirection: 'row' }}
            >
              <div style={{ flex: 1 }}>
                <div className="body-s bold mb1 text-black-tint-40">
                  View Responses to
                </div>
                <BlockSelector
                  selectBlock={this.selectBlock}
                  template={template}
                  activeBlock={rollupState.filterQuestion}
                />
              </div>
              <div style={{ flex: 1 }} className="ml2">
                <div className="body-s bold mb1 text-black-tint-40">
                  Display
                </div>
                <ShowModeSelector
                  setShowMode={this.setShowMode}
                  showMode={rollupState.showMode}
                />
              </div>
            </div>
          )}
          {rollupState.showMode === SHOW_WORKBOOK ? (
            <>
              <Worksheet
                template={template}
                doc={doc}
                data={data}
                readOnlyWorksheet={
                  currentUser.readOnly && !currentUser.isInstructor
                }
                currentUser={{
                  ...currentUser,
                  rollup: false,
                }}
              />

              {template.subtype === 'code-sled' && (
                <CodeKernelFooter
                  doc={doc}
                  worksheet={template}
                  kernelState={data.kernelState}
                  readOnlyWorksheet={
                    currentUser.readOnly && !currentUser.isInstructor
                  }
                />
              )}
            </>
          ) : (
            <Blocks
              docs={docs}
              doc={doc}
              currentUser={{ ...currentUser, timeline: true }}
              blocks={template.blocks}
              register={this.register}
              isRollup={rollupState.showMode !== SHOW_WORKBOOK}
              readOnlyWorksheet={rollupState.showMode === SHOW_WORKBOOK}
              filterQuestion={rollupState.filterQuestion}
              pins={rollupState.pins}
              changePinState={this.changePinState}
              onlyShowPinned={rollupState.showMode === SHOW_ONLY_PINNED}
            />
          )}
        </ColumnWrapper>
      </div>
    ) : null;
  }
}

export default withShareDB()(RollupWorkbook);
